import React, { CSSProperties, PropsWithChildren, ReactNode, Ref } from 'react';
import { defined } from '@unifocus/js-utils';
import { PaddingType, Panel } from '../../panel';
import { Property } from 'csstype';

export type GapProperty = boolean | 'small' | 'medium' | 'large';

export type GridTemplateDefinition = {
   columns: string[];
   rows: string[];
   areas: string[];
   justifyItems?: Property.JustifyItems;
   justifyContent?: Property.JustifyContent;
   alignItems?: Property.AlignItems;
   alignContent?: Property.AlignContent;
   autoRows?: string;
   autoColumns?: string;
   autoFlow?: string;
   autoOverflowAreas?: string[] | boolean;
}

export interface GridTemplatePanelProps {

   /**
    * The template to use.
    */
   template: GridTemplateDefinition;

   /**
    * The gap between the columns and rows.
    *
    * @defaultvalue medium
    */
   gap?: GapProperty;

   /**
    * The gap between the columns.
    *
    * @defaultvalue medium
    */
   hgap?: GapProperty;

   /**
    * The gap between the rows.
    *
    * @defaultvalue medium
    */
   vgap?: GapProperty;

   /**
    * Determines if the component has a border around it.
    *
    * @defaultvalue false
    */
   border?: boolean;

   /**
    * Determines if the panel has padding around the content.
    *
    * @defaultvalue false
    */
   padding?: boolean | 'small' | 'medium' | 'large';

   /**
    * Determines if the panel has left padding
    *
    * @defaultvalue false
    */
   paddingLeft?: PaddingType;

   /**
    * Determines if the panel has right padding
    *
    * @defaultvalue false
    */
   paddingRight?: PaddingType;

   /**
    * Determines if the panel has top padding
    *
    * @defaultvalue false
    */
   paddingTop?: PaddingType;

   /**
    * Determines if the panel has bottom padding
    *
    * @defaultvalue false
    */
   paddingBottom?: PaddingType;

   /**
    * Used to enable the built in overflow configuration for the content area.
    * @defaultvalue false
    */
   scroll?: boolean;

   /**
    * Determines if the panel will fill it's parent containers width and height.
    *
    * @defaultvalue false
    */
   fill?: boolean;

   /**
    * Determines if the panel will fill it's parent containers width.
    *
    * @defaultvalue false
    */
   horizontalFill?: boolean;

   /**
    * Determines if the panel will fill it's parent containers height.
    *
    * @defaultvalue false
    */
   verticalFill?: boolean;

   /**
    * The width of the panel.
    *
    * @defaultvalue 100%
    */
   width?: string | number;

   /**
    * The height of the panel.
    *
    * @defaultvalue 100%
    */
   height?: string | number;

   /**
    * The minimum width of the panel.
    *
    * @defaultvalue 0
    */
   minWidth?: string | number;

   /**
    * The mimimum height of the panel.
    *
    * @defaultvalue 0
    */
   minHeight?: string | number;

   /**
    * The maximum width of the panel.
    *
    * @defaultvalue undefined
    */
   maxWidth?: string | number;

   /**
    * The maximum height of the panel.
    *
    * @defaultvalue undefined
    */
   maxHeight?: string | number;

   /**
    * Sets a className on the outer panel.
    */
   className?: string;

   /**
    * Sets styles on the outer panel.
    */
   style?: CSSProperties;

   /**
    * Sets the reference property
    */
   ref?: Ref<HTMLDivElement>;

   /**
    * Sets the id of the element in the DOM.
    */
   id?: string;

   /**
    * The children TemplateAreas
    */
   children?: ReactNode;
}

export const GridTemplatePanel = React.forwardRef<HTMLDivElement, GridTemplatePanelProps>(
   (props: GridTemplatePanelProps, ref: Ref<HTMLDivElement>) => {

      const horizontalFill = defined(props.horizontalFill) ? props.horizontalFill : defined(props.fill) ? props.fill : false;
      const verticalFill = defined(props.verticalFill) ? props.verticalFill : defined(props.fill) ? props.fill : false;

      const hgap = defined(props.hgap) ? props.hgap : props.gap;
      const vgap = defined(props.vgap) ? props.vgap : props.gap;

      const template = props.template;

      let classNames = 'uf-template-panel uf-template-panel-grid';

      if (props.className) {
         classNames += ' ' + props.className;
      }

      if (hgap === true) {
         classNames += ` uf-template-panel-grid-hgap-medium`;
      }
      else if (hgap) {
         classNames += ` uf-template-panel-grid-hgap-${hgap}`;
      }

      if (vgap === true) {
         classNames += ` uf-template-panel-grid-vgap-medium`;
      }
      else if (vgap) {
         classNames += ` uf-template-panel-grid-vgap-${vgap}`;
      }

      const style: CSSProperties = {
         ...props.style,
         gridTemplateColumns: template.columns.map(it => `${it}`).join(' '),
         gridTemplateRows: template.rows.map(it => `${it}`).join(' '),
         gridTemplateAreas:  template.areas.map(it => `'${it}'`).join(' '),
         gridAutoRows:  template.autoRows,
         gridAutoColumns:  template.autoColumns,
         gridAutoFlow:  template.autoFlow,
         justifyContent:  template.justifyContent,
         justifyItems:  template.justifyItems,
         alignItems:  template.alignItems,
         alignContent:  template.alignContent
      }

      return (<Panel
         className={classNames}
         border={props.border}
         padding={props.padding}
         paddingLeft={props.paddingLeft}
         paddingRight={props.paddingRight}
         paddingTop={props.paddingTop}
         paddingBottom={props.paddingBottom}
         scroll={props.scroll}
         width={props.width}
         height={props.height}
         minHeight={props.minHeight}
         minWidth={props.minWidth}
         maxHeight={props.maxHeight}
         maxWidth={props.maxWidth}
         verticalFill={verticalFill}
         horizontalFill={horizontalFill}
         style={style}
         ref={ref}
         id={props.id}
      >
         {applyGridTemplateAreaPropsToChildren(props.children, template)}
      </Panel>);
   }
);

const applyGridTemplateAreaPropsToChildren = (children: ReactNode,
                                              template: GridTemplateDefinition) => {
   return React.Children.map(children, (child, index) => {
      const templateArea = child as React.ReactElement;
      const { areaName, align, justify, className } = templateArea.props;

      const style: CSSProperties = {
         ...templateArea.props.style
      };

      if (areaName) {
         style.gridArea = areaName;

         const autoOverflowAreas = template.autoOverflowAreas;

         if (autoOverflowAreas && (autoOverflowAreas === true || autoOverflowAreas.indexOf(areaName) >= 0)) {
            style.overflowY = 'auto';
         }
      }

      if (align) {
         style.alignSelf = align;
      }

      if (justify) {
         style.justifySelf = justify;
      }

      return React.cloneElement(templateArea,
                                {
                                   style: style,
                                   className: className,
                                   areaName: areaName,
                                   key: areaName
                                });
   });
};
